import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthenticated, logout as logoutAction } from '../redux/features/authSlice';
import { authApi } from '../redux/services/auth';
import { RootState } from '../redux/store';

const mainServiceUrl = import.meta.env.VITE_MAIN_SERVICE_URL || '';
const REFRESH_INTERVAL_MS = 14 * 60 * 1000;

const attemptTokenRefresh = async (): Promise<boolean> => {
  try {
    const res = await fetch(`${mainServiceUrl}/login/native/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  }
};

interface AuthContextType {
  authenticated: boolean | undefined;
  user: any | null;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const { authenticated, user } = useSelector((state: RootState) => state.auth);
  const [logoutMutation] = authApi.endpoints.logout.useMutation();

  const verifySession = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch(`${mainServiceUrl}/auth/verify`, {
        method: 'GET',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.authenticated && data?.user) {
          dispatch(setAuthenticated({ user: data.user }));
          return true;
        }
      }
    } catch {}
    return false;
  }, [dispatch]);

  const refreshAuth = useCallback(async () => {
    const verified = await verifySession();
    if (verified) return;
    const refreshed = await attemptTokenRefresh();
    if (refreshed) {
      await verifySession();
    } else {
      dispatch(logoutAction());
    }
  }, [verifySession, dispatch]);

  useEffect(() => {
    void refreshAuth();
  }, []);

  useEffect(() => {
    if (authenticated !== true) return;
    const interval = setInterval(() => {
      void attemptTokenRefresh();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [authenticated]);

  const logout = useCallback(async () => {
    try {
      await logoutMutation().unwrap();
    } catch {}
    dispatch(logoutAction());
    dispatch(authApi.util.resetApiState());
    window.location.href = '/login';
  }, [dispatch, logoutMutation]);

  return (
    <AuthContext.Provider value={{ authenticated, user, logout, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
